import { type AccessControlConfig } from '../../../legacy/components/AccessControl/accessControl.utils'
import { type NavMenuHierarchyL1, type NavMenuHierarchyL2 } from '../nav-v2/NavMenuHierarchy'

export const COLUMN_WIDTHS = {
  PERMISSION: '34%',
  DESCRIPTION: '33%',
  ACCESS_CONTROL: '33%',
} as const

export const getPermissions = (
  accessControl?: AccessControlConfig | AccessControlConfig[]
): string[][] => {
  if (!accessControl) return []

  const accessControlArray = !Array.isArray(accessControl) ? [accessControl] : accessControl

  const permissions = accessControlArray
    .map(accessControl => {
      const accessControlpermissions = !Array.isArray(accessControl.permissions)
        ? [accessControl.permissions]
        : accessControl.permissions

      return (accessControlpermissions as string[]) || []
    })
    .filter(permission => permission.length > 0)

  return permissions
}

export const getDescendants = (
  item: NavMenuHierarchyL1 | NavMenuHierarchyL2
): NavMenuHierarchyL2[] => {
  if (!item.children) return [item as NavMenuHierarchyL2]

  return item.children.flatMap(child => {
    if (!child.children) {
      // This child is a leaf node, include it
      return [child as NavMenuHierarchyL2]
    } else {
      // This child has children, get its leaf descendants
      return getDescendants(child)
    }
  })
}

export const numSelectedDescendants = (
  item: NavMenuHierarchyL1 | NavMenuHierarchyL2,
  selectedPermissionSlugs: Set<string>
): number => {
  const descendants = getDescendants(item)

  return descendants
    .filter(descendant =>
      getPermissions(descendant.accessControl).some(permissions => permissions.length > 0)
    )
    .filter(descendant =>
      getPermissions(descendant.accessControl).some(permissions =>
        new Set(permissions).isSubsetOf(selectedPermissionSlugs)
      )
    ).length
}

export const hasSelectedDescendants = (
  item: NavMenuHierarchyL1 | NavMenuHierarchyL2,
  selectedPermissionSlugs: Set<string>
) => numSelectedDescendants(item, selectedPermissionSlugs) > 0

// the way this method works is it iterates through each L1 Nav entry (the top level items in the nav menu)
// for each L1 entry, it "trims" out any tools (L3 items) that are not enabled by the set of permissions
// in selectedPermissionSlugs. If an L1 item has all of its tools trimmed out, it will also be trimmed out.
export const trimTree = (
  item: NavMenuHierarchyL1 | NavMenuHierarchyL2,
  selectedPermissionSlugs: Set<string>
) => {
  if (item.children) {
    item.children = item.children.filter(child =>
      hasSelectedDescendants(child, selectedPermissionSlugs)
    )
    item.children.forEach(child => trimTree(child, selectedPermissionSlugs))
  }
}
